// Find-in-thread maths, free of the DOM so it can be unit tested.
//
// The thread is a mix of React-rendered text and sanitised HTML, so the UI
// layer (components/FindInThread.tsx) concatenates every visible text node's
// value, calls findMatches() on that one string, then mapOffsets() turns each
// match back into (node, offset) pairs for a DOM Range. A match can span
// several text nodes ("<b>Inv</b>oice").

export interface TextRange {
  start: number;
  end: number;
}

export interface NodeRange {
  startNode: number;
  startOffset: number;
  endNode: number;
  endOffset: number;
}

const MAX_MATCHES = 500;

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Case-insensitive, non-overlapping matches of `needle` in `haystack`.
 *  Offsets refer to the ORIGINAL string (no lower-casing of the haystack, so
 *  characters that change length when lower-cased can't shift them). */
export function findMatches(haystack: string, needle: string): TextRange[] {
  const n = needle.trim();
  if (!n || !haystack) return [];
  const re = new RegExp(escapeRegExp(n), 'gi');
  const out: TextRange[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(haystack)) !== null) {
    if (m[0].length === 0) {
      re.lastIndex += 1;
      continue;
    }
    out.push({ start: m.index, end: m.index + m[0].length });
    if (out.length >= MAX_MATCHES) break;
  }
  return out;
}

/**
 * Maps offsets in the concatenation of nodes (with the given lengths) back to
 * node-relative positions. `matches` must be sorted and non-overlapping, as
 * findMatches returns them. A match ending exactly at a node boundary ends at
 * the END of that node, not the start of the next.
 */
export function mapOffsets(nodeLengths: readonly number[], matches: readonly TextRange[]): NodeRange[] {
  const starts: number[] = [];
  let total = 0;
  for (const len of nodeLengths) {
    starts.push(total);
    total += len;
  }

  const out: NodeRange[] = [];
  let i = 0;
  for (const m of matches) {
    if (m.start >= total || m.end > total || m.end <= m.start) continue;
    while (i < nodeLengths.length - 1 && starts[i] + nodeLengths[i] <= m.start) i++;
    const startNode = i;
    let j = i;
    while (j < nodeLengths.length - 1 && starts[j] + nodeLengths[j] < m.end) j++;
    out.push({
      startNode,
      startOffset: m.start - starts[startNode],
      endNode: j,
      endOffset: m.end - starts[j],
    });
  }
  return out;
}
